import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { NotificationManager } from 'react-notifications';
import fetchHook from '../../hooks/fetchMSSQL-hook';
import { NavigationContext } from '../../context/navigation-context';
import NoResults from '../../components/NoResults/NoResults';
import SearchFilter from '../../components/Filter/SearchFilter';
import moment from 'moment';

export default function Notifications() {
  const { fetchMssqlApi } = fetchHook();
  const { setHeaderData } = useContext(NavigationContext);
  const [notifications, setNotifications] = useState([]);
  const [totalNotificationsCount, setTotalNotificationsCount] = useState(0);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [selectedNotificationIds, setSelectedNotificationIds] = useState([]);

  const defaultFilter = {
    name: 'notificationsList',
    page: 1,
    dateTimeRangeFrom: moment().startOf("day").format("YYYY-MM-DDTHH:mm:ss"), 
    dateTimeRangeTo: moment().endOf("day").format("YYYY-MM-DDTHH:mm:ss"), 
    rowsPerPage: 50,
    isRead: 'null',
    notificationName: '',
    notificationId: null,
    notificationLevelId: null,
    notificationDescription: '',
    notificationLevelName: '',
    sortBy: 'CreateDateTime|DESC',
  };

  const [filter, setFilter] = useState(defaultFilter);
  const [settingsFilter, setSettingsFilter] = useState({
    saveFilters: true,
    autoLoading: true,
    additionalFilter: true,
    flatView: false,
    rowsPerPage: 50,
  });

  const rowsPerPage = Number(settingsFilter.rowsPerPage || filter.rowsPerPage || 50);
  const page = Math.max(1, Number(filter.page) || 1);
  const pagedNotifications = notifications.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const isRead = (notification) => (
  Number(notification.IsRead) === 1 || Boolean(notification.ReadDateTime)
);

  const visibleNotificationIds = pagedNotifications
    .filter((notification) => !isRead(notification) && notification.MessageUserId)
    .map((notification) => String(notification.MessageUserId));

  const allVisibleSelected = visibleNotificationIds.length > 0
    && visibleNotificationIds.every((id) => selectedNotificationIds.includes(id));

    const searchNotifications = (f) => {
    fetchMssqlApi(
        'user-notifications',
        {
        method: 'POST',
        hideNotification: true,
        data: {
            isRead: parseThreeState(f?.isRead),

            fromDateTime: f?.dateTimeRangeFrom || null,
            toDateTime: f?.dateTimeRangeTo || null,

            notificationName: f?.notificationName || null,
            notificationLevelName: f?.notificationLevelName || null,
            notificationDescription:
            f?.notificationDescription || null,

            page: f?.page || 1,

            rowsPerPage: Number(
            settingsFilter.rowsPerPage ||
            f?.rowsPerPage ||
            50
            ),

            sortBy: f?.sortBy || null,
        },
        },
        (response) => {
        setNotifications(getRows(response));
        setSelectedNotificationIds([]);
        },
        () => {
        NotificationManager.error(
            'Nie udało się pobrać powiadomień',
            'Powiadomienia'
        );
        }
    );
    };

  const loadNotificationsCount = () => {
    fetchMssqlApi(
      'user-notifications',
      {
        method: 'POST',
        hideNotification: true,
        data: {
          isRead: null,
          notificationName: null,
          notificationDescription: null,
          notificationLevelName: null,
          sortBy: 'CreateDateTime|DESC',
        },
      },
      (response) => {
        const rows = getRows(response);

        setTotalNotificationsCount(rows.length);
        setUnreadNotificationsCount(
          rows.filter((notification) => !isRead(notification)).length
        );
      }
    );
  };

  const markAsRead = (messageUserId) => new Promise((resolve, reject) => {
    fetchMssqlApi(
      `user-notification/${messageUserId}`,
      {
        method: 'POST',
        hideNotification: true,
      },
      resolve,
      reject
    );
  });

  const handleMarkAsRead = (messageUserId) => {
    markAsRead(messageUserId)
      .then(() => {
        NotificationManager.success(
          'Oznaczono powiadomienie jako odczytane',
          'Powiadomienia'
        );
        searchNotifications(filter);
        loadNotificationsCount();
      })
      .catch(() => NotificationManager.error(
        'Nie udało się oznaczyć powiadomienia jako odczytane',
        'Powiadomienia'
      ));
  };

  const handleMarkSelectedAsRead = () => {
    if (!selectedNotificationIds.length) return;

    Promise.all(selectedNotificationIds.map(markAsRead))
      .then(() => {
        NotificationManager.success(
          'Oznaczono zaznaczone powiadomienia jako odczytane',
          'Powiadomienia'
        );
        setSelectedNotificationIds([]);
        searchNotifications(filter);
        loadNotificationsCount();
      })
      .catch(() => NotificationManager.error(
        'Nie udało się oznaczyć zaznaczonych powiadomień jako odczytane',
        'Powiadomienia'
      ));
  };

  const handleToggleNotification = (messageUserId) => {
    const id = String(messageUserId);

    setSelectedNotificationIds((prev) => (
      prev.includes(id)
        ? prev.filter((selectedId) => selectedId !== id)
        : [...prev, id]
    ));
  };

  const handleToggleVisible = () => {
    setSelectedNotificationIds((prev) => {
      if (allVisibleSelected) {
        return prev.filter((id) => !visibleNotificationIds.includes(id));
      }

      return [...new Set([...prev, ...visibleNotificationIds])];
    });
  };

  const sortDefaults = {
    Name: 'ASC',
    LevelName: 'ASC',
    CreateDateTime: 'DESC',
    ReadDateTime: 'DESC',
  };

  const getSortState = () => {
    const [field, direction] = String(filter.sortBy || '').split('|');
    return { field, direction: String(direction || '').toUpperCase() };
  };

  const sortState = getSortState();

  const arrow = (field) => {
    if (sortState.field !== field) return '';
    return sortState.direction === 'ASC' ? ' ▲' : ' ▼';
  };

  const handleHeaderSort = (field) => {
    const [currentField, currentDirectionRaw] = String(filter.sortBy || '').split('|');
    const currentDirection = String(currentDirectionRaw || '').toUpperCase();

    const direction = currentField === field
      ? (currentDirection === 'ASC' ? 'DESC' : 'ASC')
      : sortDefaults[field];

    const nextFilter = {
      ...filter,
      page: 1,
      sortBy: `${field}|${direction}`,
    };

    setFilter(nextFilter);
    searchNotifications(nextFilter);
  };

  const getRows = (response) => {
  if (Array.isArray(response?.[0])) return response[0];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const parseThreeState = (value) => {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  return null;
};

const formatDate = (value) => {
  if (!value) return '';
  return String(value).replace('T', ' ').slice(0, 19);
};

const getFirstValue = (value) => {
  if (value === null || value === undefined || value === '') return '';

  if (Array.isArray(value)) {
    return value.length ? getFirstValue(value[0]) : '';
  }

  if (typeof value === 'object') {
    const firstValue = Object.values(value).find(
      (item) => item !== null && item !== undefined && item !== ''
    );

    return firstValue === undefined ? '' : getFirstValue(firstValue);
  }

  return String(value);
};

const getDetails = (details) => {
  if (!details) return '';

  if (typeof details === 'object') {
    return getFirstValue(details);
  }

  try {
    return getFirstValue(JSON.parse(details));
  } catch (error) {
    return String(details);
  }
};



  useEffect(() => {
    setHeaderData({ text: 'Powiadomienia' });
    loadNotificationsCount();
  }, []);

  return (
    <div>
      <div className="container-fluid p-0 filter-icon-fix mt-3">
        <ul className="nav nav-tabs machine-tabs mb-1">
          <li className="nav-item">
            <Link to="/notifications">
              <button className="nav-link btn active" tabIndex="0">
                Powiadomienia
              </button>
            </Link>
          </li>
          <li className="nav-item">
            <Link to="/notifications-settings">
              <button className="nav-link btn" tabIndex="0">
                Ustawienia
              </button>
            </Link>
          </li>
        </ul>
      </div>

      <SearchFilter
        filterName="notificationsList"
        defaultFilter={defaultFilter}
        onFilterChange={(nextFilter) => setFilter((prev) => ({ ...prev, ...nextFilter }))}
        onSettingsChange={(settings) => setSettingsFilter((prev) => ({ ...prev, ...settings }))}
        search={searchNotifications}
        loading={true}
        totalCount={notifications.length}
        dataList={notifications}
        forNow
      />

      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center header-size-fix">
            <div className="h6 mb-0">
              {totalNotificationsCount > 0 && (
                <span className="ml-2">
                  Odczytane: {totalNotificationsCount - unreadNotificationsCount}
                  {' / '}
                  {totalNotificationsCount}
                </span>
              )}

              {selectedNotificationIds.length > 0 && (
                <span className="badge badge-primary text-white ml-3">
                  Zaznaczone: {selectedNotificationIds.length}
                </span>
              )}
            </div>

            {selectedNotificationIds.length > 0 && (
              <button
                type="button"
                className="btn text-nowrap btn-outline-primary mt-1 mb-1 mr-2"
                onClick={handleMarkSelectedAsRead}
              >
                Oznacz jako odczytane
              </button>
            )}
          </div>
        </div>

        <div
          className="overflow-auto custom-scroll"
          style={{ maxHeight: 'calc(100vh - 216px)', overflowY: 'auto' }}
        >
          {notifications.length > 0 ? (
            <table className="table table-striped mb-0">
              <thead>
                <tr className="text-center headers-color">
                  <th style={{ width: '44px' }}>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      disabled={!visibleNotificationIds.length}
                      onChange={handleToggleVisible}
                    />
                  </th>
                  <th
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleHeaderSort('Name')}
                  >
                    Nazwa powiadomienia{arrow('Name')}
                  </th>
                  <th>Opis</th>
                  <th
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleHeaderSort('LevelName')}
                  >
                    Poziom powiadomienia{arrow('LevelName')}
                  </th>
                  <th
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleHeaderSort('CreateDateTime')}
                  >
                    Data utworzenia{arrow('CreateDateTime')}
                  </th>
                  <th
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleHeaderSort('ReadDateTime')}
                  >
                    Data odczytania{arrow('ReadDateTime')}
                  </th>
                  <th>Akcja</th>
                </tr>
              </thead>
              <tbody>
                {pagedNotifications.map((notification) => {
                  const messageUserId = notification.MessageUserId;
                  const notificationIsRead = isRead(notification);
                  const details = getDetails(notification.Details);
                  const selected = selectedNotificationIds.includes(String(messageUserId));

                  return (
                    <tr
                      key={messageUserId}
                      className={`text-center ${notificationIsRead ? '' : 'font-weight-bold'}`}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={notificationIsRead || !messageUserId}
                          onChange={() => handleToggleNotification(messageUserId)}
                        />
                      </td>
                      <td>{notification.Name || notification.NotificationName || '-'}</td>
                      <td className="text-left">
                        {notification.Description || '-'}
                        {details && (
                          <div className="text-muted small mt-1 font-weight-normal">
                            {details}
                          </div>
                        )}
                      </td>
                      <td>
                        {notification.NotificationLevelName && (
                          <span
                            className="badge bg-primary text-white"
                            style={{ padding: '9px', fontSize: '13px' }}
                          >
                            {notification.NotificationLevelName}
                          </span>
                        )}
                      </td>
                      <td>{formatDate(notification.CreateDateTime)}</td>
                      <td>{formatDate(notification.ReadDateTime)}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          disabled={notificationIsRead || !messageUserId}
                          onClick={() => handleMarkAsRead(messageUserId)}
                        >
                          Oznacz jako odczytane
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <NoResults />
          )}
        </div>
      </div>
    </div>
  );
}
